import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { countOrACVFormatterMillionFixedOne } from '../../util/customFunctions';
import { updatedScheme10 } from '../../util/chartColorScheme';

/**
 * Paper 6 - Deal Progression and Closures
 * Contains 3 charts:
 * 1. Cumulative Closed Won by Month (line chart)
 * 2. Pipeline percent by stage at start of each Quarter (stacked bar)
 * 3. Forecast accuracy - Actual Won / Forecast at Week 1 (bar chart with target line)
 */

// Chart 1: Cumulative Closed Won by Month
export const CumulativeClosedWonChart = ({ data, fiscalYear }) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!data || !chartRef.current) return;

    d3.select(chartRef.current).selectAll('*').remove();

    const margin = { top: 40, right: 30, bottom: 50, left: 70 };
    const width = 1000 - margin.left - margin.right;
    const height = 280 - margin.top - margin.bottom;

    const svg = d3
      .select(chartRef.current)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // X Scale
    const xScale = d3
      .scaleBand()
      .domain(data.map(d => d.month))
      .range([0, width])
      .padding(0.1);

    // Y Scale - include quota in max calculation
    const maxActual = d3.max(data, d => d.cumulativeActual) || 0;
    const maxQuota = d3.max(data, d => d.cumulativeQuota || 0) || 0;
    const maxValue = Math.max(maxActual, maxQuota) * 1.1 || 100;
    const yScale = d3
      .scaleLinear()
      .domain([0, maxValue])
      .range([height, 0]);

    // Grid lines
    svg
      .append('g')
      .attr('class', 'grid')
      .call(d3.axisLeft(yScale).ticks(5).tickSize(-width).tickFormat(''))
      .selectAll('line')
      .style('stroke', '#e0e0e0')
      .style('stroke-opacity', 0.7);

    svg.selectAll('.domain').remove();

    // X Axis
    svg
      .append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(xScale).tickSize(0))
      .selectAll('text')
      .style('font-size', '10px');

    // Y Axis
    svg
      .append('g')
      .call(d3.axisLeft(yScale).ticks(5).tickFormat(d => countOrACVFormatterMillionFixedOne(d, true)))
      .selectAll('text')
      .style('font-size', '10px');

    // Filter data with values for the lines
    const validData = data.filter(d => d.cumulativeActual > 0);
    const validQuotaData = data.filter(d => (d.cumulativeQuota || 0) > 0);

    // Line generator for actual
    const lineActual = d3
      .line()
      .x(d => xScale(d.month) + xScale.bandwidth() / 2)
      .y(d => yScale(d.cumulativeActual))
      .curve(d3.curveMonotoneX);

    // Line generator for quota target
    const lineQuota = d3
      .line()
      .x(d => xScale(d.month) + xScale.bandwidth() / 2)
      .y(d => yScale(d.cumulativeQuota || 0))
      .curve(d3.curveMonotoneX);

    // Draw quota target line (if quota data exists)
    if (validQuotaData.length > 0) {
      svg
        .append('path')
        .datum(validQuotaData)
        .attr('fill', 'none')
        .attr('stroke', '#bf9000')
        .attr('stroke-width', 2.5)
        .attr('d', lineQuota);

      // Draw quota points
      svg
        .selectAll('.point-quota')
        .data(validQuotaData)
        .enter()
        .append('circle')
        .attr('class', 'point-quota')
        .attr('cx', d => xScale(d.month) + xScale.bandwidth() / 2)
        .attr('cy', d => yScale(d.cumulativeQuota || 0))
        .attr('r', 4)
        .attr('fill', '#bf9000')
        .attr('stroke', '#fff')
        .attr('stroke-width', 1.5);

      // Value labels on quota points with overlap detection
      svg
        .selectAll('.point-label-quota')
        .data(validQuotaData)
        .enter()
        .append('text')
        .attr('class', 'point-label-quota')
        .attr('x', d => xScale(d.month) + xScale.bandwidth() / 2)
        .attr('y', d => {
          const quotaY = yScale(d.cumulativeQuota || 0);
          const actualY = yScale(d.cumulativeActual || 0);
          const diff = Math.abs(quotaY - actualY);

          // If points are close (within 25 pixels), position quota label below
          // Otherwise position it above the point
          if (diff < 25) {
            return quotaY + 18;
          } else if (d.cumulativeQuota > d.cumulativeActual) {
            // Quota is higher, put label above
            return quotaY - 10;
          } else {
            // Quota is lower, put label below
            return quotaY + 15;
          }
        })
        .attr('text-anchor', 'middle')
        .attr('font-size', '9px')
        .attr('fill', '#bf9000')
        .attr('font-weight', 'bold')
        .text(d => ((d.cumulativeQuota || 0) / 1000000).toFixed(1));
    }

    // Draw actual line
    svg
      .append('path')
      .datum(validData)
      .attr('fill', 'none')
      .attr('stroke', '#70ad47')
      .attr('stroke-width', 2.5)
      .attr('d', lineActual);

    // Draw actual points
    svg
      .selectAll('.point-actual')
      .data(validData)
      .enter()
      .append('circle')
      .attr('class', 'point-actual')
      .attr('cx', d => xScale(d.month) + xScale.bandwidth() / 2)
      .attr('cy', d => yScale(d.cumulativeActual))
      .attr('r', 4)
      .attr('fill', '#70ad47')
      .attr('stroke', '#fff')
      .attr('stroke-width', 1.5);

    // Value labels on actual points with overlap detection
    svg
      .selectAll('.point-label')
      .data(validData)
      .enter()
      .append('text')
      .attr('class', 'point-label')
      .attr('x', d => xScale(d.month) + xScale.bandwidth() / 2)
      .attr('y', d => {
        const actualY = yScale(d.cumulativeActual);
        const quotaY = yScale(d.cumulativeQuota || 0);
        const diff = Math.abs(actualY - quotaY);

        // If points are close (within 25 pixels) and quota exists, position actual label above more
        if (d.cumulativeQuota > 0 && diff < 25) {
          return actualY - 18;
        } else {
          return actualY - 10;
        }
      })
      .attr('text-anchor', 'middle')
      .attr('font-size', '9px')
      .attr('fill', '#333')
      .text(d => (d.cumulativeActual / 1000000).toFixed(1));

    // Title
    svg
      .append('text')
      .attr('x', width / 2)
      .attr('y', -15)
      .attr('text-anchor', 'middle')
      .attr('font-size', '13px')
      .attr('font-weight', 'bold')
      .text('Cumulative Closed Won ($M)');

    // Y axis label
    svg
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -height / 2)
      .attr('y', -50)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .text('ACV ($M)');

    // Legend
    const legendData = [
      { label: 'Actual', color: '#70ad47' },
      { label: 'Quota', color: '#bf9000' }
    ];

    const legend = svg
      .append('g')
      .attr('class', 'legend')
      .attr('transform', `translate(${width / 2 - 60}, ${height + 35})`);

    legendData.forEach((item, i) => {
      const legendItem = legend
        .append('g')
        .attr('transform', `translate(${i * 80}, 0)`);

      // Legend color box
      legendItem
        .append('rect')
        .attr('x', 0)
        .attr('y', -6)
        .attr('width', 12)
        .attr('height', 12)
        .attr('fill', item.color);

      // Legend text
      legendItem
        .append('text')
        .attr('x', 18)
        .attr('y', 4)
        .attr('font-size', '11px')
        .attr('fill', '#333')
        .text(item.label);
    });

  }, [data, fiscalYear]);

  return <div ref={chartRef} id="printChart" />;
};

// Chart 2: Pipeline percent by stage at start of each Quarter
export const PipelineByStageChart = ({ data, currentFiscalQuarter, stageCategories }) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!data || !chartRef.current || !stageCategories) return;

    d3.select(chartRef.current).selectAll('*').remove();

    const margin = { top: 40, right: 30, bottom: 50, left: 60 };
    const width = 450 - margin.left - margin.right;
    const height = 280 - margin.top - margin.bottom;

    const svg = d3
      .select(chartRef.current)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Use the same color scheme as D3WonStackBar
    const colorScale = d3.scaleOrdinal(updatedScheme10);
    const colors = {};
    stageCategories.forEach((category, index) => {
      const categoryKey = category.toLowerCase().replace(/\+/g, '').replace(/\s+/g, '');
      colors[categoryKey] = colorScale(category);
    });

    // X Scale
    const xScale = d3
      .scaleBand()
      .domain(data.map(d => d.quarterLabel))
      .range([0, width])
      .padding(0.3);

    // Y Scale (0-100%)
    const yScale = d3
      .scaleLinear()
      .domain([0, 100])
      .range([height, 0]);

    // Grid lines
    svg
      .append('g')
      .attr('class', 'grid')
      .call(d3.axisLeft(yScale).ticks(5).tickSize(-width).tickFormat(''))
      .selectAll('line')
      .style('stroke', '#e0e0e0')
      .style('stroke-opacity', 0.7);

    svg.selectAll('.domain').remove();

    // X Axis
    svg
      .append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(xScale).tickSize(0))
      .selectAll('text')
      .style('font-size', '11px')
      .style('font-weight', d => {
        const quarter = data.find(q => q.quarterLabel === d);
        return quarter && quarter.quarter === currentFiscalQuarter ? 'bold' : 'normal';
      });

    // Y Axis
    svg
      .append('g')
      .call(d3.axisLeft(yScale).ticks(5).tickFormat(d => `${d}%`))
      .selectAll('text')
      .style('font-size', '10px');

    // Stack generator with dynamic keys
    const stackKeys = stageCategories.map(category =>
      category.toLowerCase().replace(/\+/g, '').replace(/\s+/g, '')
    );

    const stack = d3.stack()
      .keys(stackKeys)
      .order(d3.stackOrderNone)
      .offset(d3.stackOffsetNone);

    const series = stack(data);

    // Draw stacked bars
    svg
      .selectAll('g.layer')
      .data(series)
      .enter()
      .append('g')
      .attr('class', 'layer')
      .attr('fill', d => colors[d.key])
      .selectAll('rect')
      .data(d => d)
      .enter()
      .append('rect')
      .attr('x', d => xScale(d.data.quarterLabel))
      .attr('y', d => yScale(d[1]))
      .attr('height', d => yScale(d[0]) - yScale(d[1]))
      .attr('width', xScale.bandwidth())
      .style('opacity', d => {
        // Dim future quarters
        if (d.data.quarter > currentFiscalQuarter) return 0.5;
        return 1;
      });

    // Add percentage labels on bars
    svg
      .selectAll('g.label-layer')
      .data(series)
      .enter()
      .append('g')
      .attr('class', 'label-layer')
      .selectAll('text')
      .data(d => d)
      .enter()
      .append('text')
      .attr('x', d => xScale(d.data.quarterLabel) + xScale.bandwidth() / 2)
      .attr('y', d => yScale(d[1]) + (yScale(d[0]) - yScale(d[1])) / 2)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('font-size', '10px')
      .attr('font-weight', 'bold')
      .attr('fill', 'white')
      .text(d => {
        const barHeight = yScale(d[0]) - yScale(d[1]);
        const percentage = d[1] - d[0];
        // Only show label if bar is tall enough (at least 20px) and percentage is non-zero
        if (barHeight >= 20 && percentage !== 0) {
          return `${Math.abs(Math.round(percentage))}%`;
        }
        return '';
      });

    // Title
    svg
      .append('text')
      .attr('x', width / 2)
      .attr('y', -15)
      .attr('text-anchor', 'middle')
      .attr('font-size', '13px')
      .attr('font-weight', 'bold')
      .text('Pipeline percent ($) in stage - at start of each Quarter');

  }, [data, currentFiscalQuarter, stageCategories]);

  return <div ref={chartRef} id="printChart" />;
};

// Chart 3: Forecast Accuracy
export const ForecastAccuracyChart = ({ data, target }) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!data || !chartRef.current) return;

    d3.select(chartRef.current).selectAll('*').remove();

    const margin = { top: 40, right: 30, bottom: 50, left: 60 };
    const width = 400 - margin.left - margin.right;
    const height = 280 - margin.top - margin.bottom;

    const svg = d3
      .select(chartRef.current)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // X Scale
    const xScale = d3
      .scaleBand()
      .domain(data.map(d => d.quarterLabel))
      .range([0, width])
      .padding(0.4);

    // Y Scale
    const maxValue = Math.max(target || 100, d3.max(data, d => d.accuracy) || 0) * 1.2;
    const yScale = d3
      .scaleLinear()
      .domain([0, maxValue])
      .range([height, 0]);

    // Grid lines
    svg
      .append('g')
      .attr('class', 'grid')
      .call(d3.axisLeft(yScale).ticks(5).tickSize(-width).tickFormat(''))
      .selectAll('line')
      .style('stroke', '#e0e0e0')
      .style('stroke-opacity', 0.7);

    svg.selectAll('.domain').remove();

    // X Axis
    svg
      .append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(xScale).tickSize(0))
      .selectAll('text')
      .style('font-size', '11px');

    // Y Axis
    svg
      .append('g')
      .call(d3.axisLeft(yScale).ticks(5).tickFormat(d => `${d}%`))
      .selectAll('text')
      .style('font-size', '10px');

    // Draw bars only for elapsed quarters
    svg
      .selectAll('.bar')
      .data(data.filter(d => d.showBar))
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('x', d => xScale(d.quarterLabel))
      .attr('y', d => yScale(d.accuracy))
      .attr('width', xScale.bandwidth())
      .attr('height', d => height - yScale(d.accuracy))
      .attr('fill', '#4caf50');

    // Value labels on bars
    svg
      .selectAll('.bar-label')
      .data(data.filter(d => d.showBar && d.accuracy > 0))
      .enter()
      .append('text')
      .attr('class', 'bar-label')
      .attr('x', d => xScale(d.quarterLabel) + xScale.bandwidth() / 2)
      .attr('y', d => yScale(d.accuracy) - 5)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('font-weight', 'bold')
      .attr('fill', '#333')
      .text(d => `${d.accuracy}%`);

    // Target line (red)
    if (target > 0) {
      svg
        .append('line')
        .attr('x1', 0)
        .attr('x2', width)
        .attr('y1', yScale(target))
        .attr('y2', yScale(target))
        .attr('stroke', '#bf9000')
        .attr('stroke-width', 2);

      // Target label
      svg
        .append('text')
        .attr('x', width - 5)
        .attr('y', yScale(target) - 5)
        .attr('text-anchor', 'end')
        .attr('font-size', '10px')
        .attr('font-weight', 'bold')
        .attr('fill', '#bf9000')
        .text('Target');
    }

    // Title
    svg
      .append('text')
      .attr('x', width / 2)
      .attr('y', -15)
      .attr('text-anchor', 'middle')
      .attr('font-size', '12px')
      .attr('font-weight', 'bold')
      .text('Forecast accuracy');

    // Subtitle
    svg
      .append('text')
      .attr('x', width / 2)
      .attr('y', -2)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('fill', '#666')
      .text('Close as % of start of quarter ACV ($M)');

  }, [data, target]);

  return <div ref={chartRef} id="printChart" />;
};

// Combined component for all 3 charts
const DealProgressionCharts = ({ data }) => {
  if (!data) return null;

  return {
    CumulativeClosedWonChart: (
      <CumulativeClosedWonChart
        data={data.cumulativeClosedWon}
        fiscalYear={data.fiscalYear}
      />
    ),
    PipelineByStageChart: (
      <PipelineByStageChart
        data={data.pipelineByStage}
        currentFiscalQuarter={data.currentFiscalQuarter}
        stageCategories={data.stageCategories}
      />
    ),
    ForecastAccuracyChart: (
      <ForecastAccuracyChart
        data={data.forecastAccuracy}
        target={data.forecastAccuracyTarget}
      />
    )
  };
};

export default DealProgressionCharts;
